import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import posthog from "@/lib/posthog";
import { trpc } from "@/trpc";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Columna } from "../PositionColumn";
import StickyScrollRail from "../StickyScrollRail";
import { headerColorForStage } from "../types";
import SolicitudCard from "./SolicitudCard";
import {
  isSolicitudFilterMode,
  type SolicitudFiltros,
  type SolicitudTicket,
  type TicketPosition,
} from "./types";

type Props = {
  merchantId: string;
  pipelineId: string;
  positions: TicketPosition[];
  filtros: SolicitudFiltros;
  // Debounced, trimmed search term (name / phone / cédula). When set, it takes
  // precedence over filter/default modes and renders non-realtime results.
  searchQuery?: string;
  startDate?: string;
  endDate?: string;
  onSelectSolicitud: (id: string) => void;
  selectedSolicitudId?: string;
  employeeNameById: Map<string, string>;
  anyFilter: boolean;
  onClearFilters: () => void;
};

// Single source of the per-column query input: the drag handler does cache
// surgery with getInfiniteData/setInfiniteData, so its keys must be built the
// exact same way as the column queries'.
const buildColumnQueryInput = (
  merchantId: string,
  pipelineId: string,
  status: string,
  startDate?: string,
  endDate?: string
) => ({ merchantId, status, pipelineId, limit: 25, startDate, endDate });

// --- Per-status column, one infinite query each (default mode) ---

const StatusColumn = ({
  merchantId,
  pipelineId,
  position,
  startDate,
  endDate,
  onSelectSolicitud,
  selectedSolicitudId,
  employeeNameById,
  onTicketsChange,
}: {
  merchantId: string;
  pipelineId: string;
  position: TicketPosition;
  startDate?: string;
  endDate?: string;
  onSelectSolicitud: (id: string) => void;
  selectedSolicitudId?: string;
  employeeNameById: Map<string, string>;
  onTicketsChange: (status: string, count: number, isLoading: boolean) => void;
}) => {
  const query = trpc.serviceRequests.listByStatus.useInfiniteQuery(
    buildColumnQueryInput(merchantId, pipelineId, position.status, startDate, endDate),
    { getNextPageParam: (lastPage) => lastPage.cursor ?? undefined }
  );

  const tickets = useMemo(
    () => query.data?.pages.flatMap((p) => p.data) ?? [],
    [query.data]
  );

  const { isLoading } = query;
  const { status } = position;
  useEffect(() => {
    onTicketsChange(status, tickets.length, isLoading);
  }, [status, tickets.length, isLoading, onTicketsChange]);

  return (
    <Columna
      title={position.displayName}
      accent={headerColorForStage(position.stage)}
      countLabel={`${tickets.length}${query.hasNextPage ? "+" : ""}`}
      droppableId={`status:${position.status}`}
      droppableDisabled={false}
      isLoading={query.isLoading}
      itemIds={tickets.map((t) => t.id)}
      hasMore={!!query.hasNextPage}
      loadingMore={query.isFetchingNextPage}
      onLoadMore={() => query.fetchNextPage()}
      emptyText="Sin solicitudes"
    >
      {tickets.map((ticket) => (
        <SolicitudCard
          key={ticket.id}
          ticket={ticket}
          selected={selectedSolicitudId === ticket.id}
          draggable
          onClick={() => onSelectSolicitud(ticket.id)}
          employeeNameById={employeeNameById}
        />
      ))}
    </Columna>
  );
};

// --- Filter mode: one query for the whole board, grouped client-side ---

const FilteredColumns = ({
  positions,
  tickets,
  isLoading,
  onSelectSolicitud,
  selectedSolicitudId,
  employeeNameById,
}: {
  positions: TicketPosition[];
  tickets: SolicitudTicket[];
  isLoading: boolean;
  onSelectSolicitud: (id: string) => void;
  selectedSolicitudId?: string;
  employeeNameById: Map<string, string>;
}) => {
  const grouped = useMemo(() => {
    const map = new Map<string, SolicitudTicket[]>();
    for (const ticket of tickets) {
      const list = map.get(ticket.status) ?? [];
      list.push(ticket);
      map.set(ticket.status, list);
    }
    return map;
  }, [tickets]);

  return (
    <>
      {positions.map((position) => {
        const columnTickets = grouped.get(position.status) ?? [];
        return (
          <Columna
            key={position.status}
            title={position.displayName}
            accent={headerColorForStage(position.stage)}
            countLabel={`${columnTickets.length}`}
            droppableId={`status:${position.status}`}
            droppableDisabled={false}
            isLoading={isLoading}
            itemIds={columnTickets.map((t) => t.id)}
            emptyText="Sin coincidencias"
          >
            {columnTickets.map((ticket) => (
              <SolicitudCard
                key={ticket.id}
                ticket={ticket}
                selected={selectedSolicitudId === ticket.id}
                draggable
                onClick={() => onSelectSolicitud(ticket.id)}
                employeeNameById={employeeNameById}
              />
            ))}
          </Columna>
        );
      })}
    </>
  );
};

const SolicitudesBoard = ({
  merchantId,
  pipelineId,
  positions,
  filtros,
  searchQuery,
  startDate,
  endDate,
  onSelectSolicitud,
  selectedSolicitudId,
  employeeNameById,
  anyFilter,
  onClearFilters,
}: Props) => {
  const utils = trpc.useUtils();
  const moveMutation = trpc.serviceRequests.moveStatus.useMutation();

  // 5px activation distance keeps card clicks from being swallowed by drag
  // detection — matches the lead board.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Search takes precedence over the category/asesor/prioridad filter mode and
  // the default per-status columns.
  const isSearchMode = !!searchQuery;
  const isFilterMode = !isSearchMode && isSolicitudFilterMode(filtros);

  const searchResults = trpc.serviceRequests.searchNonRealtime.useQuery(
    {
      merchantId,
      pipelineId,
      search: searchQuery ?? "",
      startDate,
      endDate,
    },
    { enabled: !!merchantId && isSearchMode }
  );

  const positionMetaByStatus = useMemo(() => {
    const map = new Map<string, TicketPosition>();
    for (const p of positions) map.set(p.status, p);
    return map;
  }, [positions]);

  const filterQueryKey = useMemo(
    () => ({
      merchantId,
      pipelineId,
      category: filtros.categoria || undefined,
      assignedEmployeeId: filtros.asesor || undefined,
      priority: (filtros.prioridad || undefined) as
        | "low"
        | "medium"
        | "high"
        | "urgent"
        | undefined,
      startDate,
      endDate,
      limit: 200,
    }),
    [merchantId, pipelineId, filtros, startDate, endDate]
  );

  const filteredQuery = trpc.serviceRequests.listFiltered.useQuery(filterQueryKey, {
    enabled: !!merchantId && isFilterMode,
  });

  // Loaded counts per column, reported by each StatusColumn, so the board can
  // show a single global empty state instead of a row of empty columns.
  const [statusByColumn, setStatusByColumn] = useState<
    Map<string, { count: number; isLoading: boolean }>
  >(new Map());
  const handleTicketsChange = useCallback(
    (status: string, count: number, isLoading: boolean) => {
      setStatusByColumn((prev) => {
        const existing = prev.get(status);
        if (existing && existing.count === count && existing.isLoading === isLoading) return prev;
        const next = new Map(prev);
        next.set(status, { count, isLoading });
        return next;
      });
    },
    []
  );

  const allEmpty = isSearchMode
    ? !searchResults.isLoading && (searchResults.data?.data.length ?? 0) === 0
    : isFilterMode
      ? !filteredQuery.isLoading && (filteredQuery.data?.data.length ?? 0) === 0
      : positions.length > 0 &&
        positions.every((p) => {
          const status = statusByColumn.get(p.status);
          return status && !status.isLoading && status.count === 0;
        });

  const moveTicket = async (ticketId: string, targetStatus: string, sourceStatus: string) => {
    const targetMeta = positionMetaByStatus.get(targetStatus);
    try {
      await moveMutation.mutateAsync({ merchantId, id: ticketId, status: targetStatus });
      toast.success(`Solicitud movida a ${targetMeta?.displayName ?? targetStatus}`);
      posthog.capture("tablero solicitud moved", {
        merchant_id: merchantId,
        solicitud_id: ticketId,
        pipeline_id: pipelineId,
        source_status: sourceStatus,
        target_status: targetStatus,
        target_status_name: targetMeta?.displayName,
        target_stage: targetMeta?.stage,
      });
      return true;
    } catch (error) {
      toast.error("No pudimos mover la solicitud. Intenta de nuevo.");
      console.error("moveStatus failed", error);
      return false;
    }
  };

  const parseDrag = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return undefined;
    const overId = String(over.id);
    if (!overId.startsWith("status:")) return undefined;
    const targetStatus = overId.slice("status:".length);
    const sourceStatus = active.data.current?.status as string | undefined;
    if (!sourceStatus || sourceStatus === targetStatus) return undefined;
    return { ticketId: String(active.id), sourceStatus, targetStatus };
  };

  // Default mode: two per-status caches to patch (remove from source, prepend
  // to target), rolled back together on failure.
  const handleDragEnd = async (event: DragEndEvent) => {
    const drag = parseDrag(event);
    if (!drag) return;
    const { ticketId, sourceStatus, targetStatus } = drag;

    const sourceKey = buildColumnQueryInput(
      merchantId,
      pipelineId,
      sourceStatus,
      startDate,
      endDate
    );
    const targetKey = buildColumnQueryInput(
      merchantId,
      pipelineId,
      targetStatus,
      startDate,
      endDate
    );

    const prevSource = utils.serviceRequests.listByStatus.getInfiniteData(sourceKey);
    const prevTarget = utils.serviceRequests.listByStatus.getInfiniteData(targetKey);

    const movedTicket = prevSource?.pages.flatMap((p) => p.data).find((t) => t.id === ticketId);
    if (!movedTicket) return;

    if (prevSource) {
      utils.serviceRequests.listByStatus.setInfiniteData(sourceKey, {
        ...prevSource,
        pages: prevSource.pages.map((page) => ({
          ...page,
          data: page.data.filter((t) => t.id !== ticketId),
        })),
      });
    }

    if (prevTarget) {
      const updatedTicket = { ...movedTicket, status: targetStatus };
      const [firstPage, ...restPages] = prevTarget.pages;
      const newPages = firstPage
        ? [{ ...firstPage, data: [updatedTicket, ...firstPage.data] }, ...restPages]
        : prevTarget.pages;
      utils.serviceRequests.listByStatus.setInfiniteData(targetKey, {
        ...prevTarget,
        pages: newPages,
      });
    } else {
      utils.serviceRequests.listByStatus.invalidate(targetKey);
    }

    const ok = await moveTicket(ticketId, targetStatus, sourceStatus);
    if (!ok) {
      if (prevSource) utils.serviceRequests.listByStatus.setInfiniteData(sourceKey, prevSource);
      if (prevTarget) utils.serviceRequests.listByStatus.setInfiniteData(targetKey, prevTarget);
    }
  };

  // Filter mode: one flat cache, so the patch is a single status rewrite.
  const handleFilterDragEnd = async (event: DragEndEvent) => {
    const drag = parseDrag(event);
    if (!drag) return;
    const { ticketId, sourceStatus, targetStatus } = drag;

    const prev = utils.serviceRequests.listFiltered.getData(filterQueryKey);
    if (prev) {
      utils.serviceRequests.listFiltered.setData(filterQueryKey, {
        ...prev,
        data: prev.data.map((t) => (t.id === ticketId ? { ...t, status: targetStatus } : t)),
      });
    }

    const ok = await moveTicket(ticketId, targetStatus, sourceStatus);
    if (ok) {
      // Keep the per-column caches fresh for when filters are cleared.
      utils.serviceRequests.listByStatus.invalidate();
    } else if (prev) {
      utils.serviceRequests.listFiltered.setData(filterQueryKey, prev);
    }
  };

  // Search mode: the results come from the CDC-lagged Tinybird endpoint, so no
  // cache surgery — persist the move and invalidate to refetch (matches the
  // leads non-realtime board).
  const handleSearchDragEnd = async (event: DragEndEvent) => {
    const drag = parseDrag(event);
    if (!drag) return;
    const { ticketId, sourceStatus, targetStatus } = drag;
    const ok = await moveTicket(ticketId, targetStatus, sourceStatus);
    if (ok) utils.serviceRequests.searchNonRealtime.invalidate();
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {isSearchMode ? (
        <div className="flex min-h-9 items-center justify-end gap-2 border-b border-neutral-100 bg-surface px-4 py-1">
          <span className="text-xs text-neutral-600">Datos no en tiempo real</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-neutral-600"
            onClick={() => utils.serviceRequests.searchNonRealtime.invalidate()}
          >
            <RefreshCw className="mr-1 h-3.5 w-3.5" /> Refrescar
          </Button>
        </div>
      ) : null}
      <DndContext
        sensors={sensors}
        onDragEnd={
          isSearchMode ? handleSearchDragEnd : isFilterMode ? handleFilterDragEnd : handleDragEnd
        }
      >
        <StickyScrollRail className={cn("flex min-h-0 flex-1 gap-3 p-4", allEmpty && "hidden")}>
          {isSearchMode ? (
            <FilteredColumns
              positions={positions}
              tickets={searchResults.data?.data ?? []}
              isLoading={searchResults.isLoading}
              onSelectSolicitud={onSelectSolicitud}
              selectedSolicitudId={selectedSolicitudId}
              employeeNameById={employeeNameById}
            />
          ) : isFilterMode ? (
            <FilteredColumns
              positions={positions}
              tickets={filteredQuery.data?.data ?? []}
              isLoading={filteredQuery.isLoading}
              onSelectSolicitud={onSelectSolicitud}
              selectedSolicitudId={selectedSolicitudId}
              employeeNameById={employeeNameById}
            />
          ) : (
            positions.map((position) => (
              <StatusColumn
                key={position.status}
                merchantId={merchantId}
                pipelineId={pipelineId}
                position={position}
                startDate={startDate}
                endDate={endDate}
                onSelectSolicitud={onSelectSolicitud}
                selectedSolicitudId={selectedSolicitudId}
                employeeNameById={employeeNameById}
                onTicketsChange={handleTicketsChange}
              />
            ))
          )}
        </StickyScrollRail>
        {allEmpty ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
            <p className="text-sm text-neutral-600">
              {isSearchMode
                ? "Ninguna solicitud coincide con la búsqueda"
                : "Ninguna solicitud coincide con los filtros aplicados"}
            </p>
            {anyFilter ? <Button onClick={onClearFilters}>Limpiar filtros</Button> : null}
          </div>
        ) : null}
      </DndContext>
    </div>
  );
};

export default SolicitudesBoard;
